import React, { useState } from 'react';
import { CashFlowResult } from '../../types';
import { api } from '../../services/api';
import StoryCard from '../StoryCard';
import TabNavigation, { TabType } from '../TabNavigation';
import CashFlowSimulation from '../simulations/CashFlowSimulation';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { modelStories } from '../../data/stories';

interface CashFlowModuleProps {
  data: CashFlowResult | null;
  onOptimizationComplete: (data: CashFlowResult) => void;
  onLoadingStart: () => void;
}

const CashFlowModule: React.FC<CashFlowModuleProps> = ({
  data,
  onOptimizationComplete,
  onLoadingStart,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>(data ? 'run' : 'case-study');
  const [companyId, setCompanyId] = useState('COMP84effbeed87e');
  const [horizon, setHorizon] = useState(30);
  const [minBalance, setMinBalance] = useState(1000000);

  const handleOptimize = async () => {
    onLoadingStart();
    const result = await api.optimizeCashFlow({
      company_id: companyId,
      planning_horizon_days: horizon,
      minimum_cash_balance: minBalance,
    });
    onOptimizationComplete(result);
    // Stay on run tab to show results
    setActiveTab('run');
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="space-y-6">
      <div className="section-card">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Cash Flow Optimization</h2>
        <p className="text-gray-600 mb-4">
          Optimize working capital allocation across investments and credit facilities using Linear Programming
        </p>
        
        <TabNavigation activeTab={activeTab} onTabChange={setActiveTab} />
      </div>

      {/* Case Study Tab - Always show the story */}
      {activeTab === 'case-study' && (
        <div className="section-card">
          <StoryCard story={modelStories.cashflow} />
        </div>
      )}

      {/* Run Tab */}
      {activeTab === 'run' && (
        <div className="space-y-6">
          <div className="section-card">

        <form className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div>
            <label className="label">Company ID</label>
            <input
              type="text"
              value={companyId}
              onChange={(e) => setCompanyId(e.target.value)}
              className="input-field"
            />
          </div>
          <div>
            <label className="label">Planning Horizon (days)</label>
            <input
              type="number"
              value={horizon}
              onChange={(e) => setHorizon(Number(e.target.value))}
              className="input-field"
              min="7"
              max="180"
            />
          </div>
          <div>
            <label className="label">Min Cash Balance ($)</label>
            <input
              type="number"
              value={minBalance}
              onChange={(e) => setMinBalance(Number(e.target.value))}
              className="input-field"
            />
          </div>
        </form>

        <button type="button" onClick={handleOptimize} className="btn-primary">
          Run Optimization
        </button>
      </div>

      {data && (
        <>
          {data.input_summary && (
            <div className="info-section">
              <h3 className="text-lg font-bold text-blue-900 mb-3">Input Data Summary</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {Object.entries(data.input_summary).map(([key, value]) => (
                  <div key={key}>
                    <div className="text-sm text-blue-700 capitalize">
                      {key.replace(/_/g, ' ')}
                    </div>
                    <div className="font-semibold text-blue-900">{value}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {data.model_info && (
            <div className="section-card bg-gray-50">
              <h3 className="text-lg font-bold text-gray-900 mb-3">Model Information</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <div className="text-sm text-gray-600">Algorithm</div>
                  <div className="font-semibold">{data.model_info.algorithm}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Variables</div>
                  <div className="font-semibold">{data.model_info.variables}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Constraints</div>
                  <div className="font-semibold">{data.model_info.constraints}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Iterations</div>
                  <div className="font-semibold">{data.model_info.iterations}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Confidence</div>
                  <div className="font-semibold">{(data.model_info.confidence! * 100).toFixed(0)}%</div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Solve Time</div>
                  <div className="font-semibold">{data.solve_time_seconds}s</div>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="stat-card">
              <div className="text-sm text-gray-600 mb-1">Status</div>
              <div className="text-2xl font-bold text-green-600">{data.status}</div>
            </div>
            <div className="stat-card">
              <div className="text-sm text-gray-600 mb-1">Objective Value</div>
              <div className="text-2xl font-bold text-gray-800">
                {formatCurrency(data.objective_value!)}
              </div>
            </div>
            <div className="stat-card">
              <div className="text-sm text-gray-600 mb-1">Avg Balance</div>
              <div className="text-2xl font-bold text-gray-800">
                {formatCurrency(data.summary.average_balance)}
              </div>
            </div>
            <div className="stat-card">
              <div className="text-sm text-gray-600 mb-1">Net Benefit</div>
              <div className="text-2xl font-bold text-green-600">
                {formatCurrency(data.summary.net_benefit)}
              </div>
            </div>
          </div>

          {data.llm_explanation && (
            <div className="section-card bg-purple-50 border-l-4 border-purple-500">
              <h3 className="text-lg font-bold text-purple-900 mb-3">AI Analysis</h3>
              <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                {data.llm_explanation}
              </p>
            </div>
          )}

          {data.recommendations && (
            <div className="success-section">
              <h3 className="text-lg font-bold text-green-900 mb-3">Strategic Recommendations</h3>
              <ol className="space-y-2">
                {data.recommendations.map((rec, idx) => (
                  <li key={idx} className="flex items-start">
                    <span className="font-bold text-green-700 mr-2">{idx + 1}.</span>
                    <span className="text-gray-700">{rec}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {data.risk_metrics && (
            <div className="warning-section">
              <h3 className="text-lg font-bold text-yellow-900 mb-3">Risk Assessment</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(data.risk_metrics).map(([key, value]) => (
                  <div key={key}>
                    <div className="text-sm text-yellow-700">{key}</div>
                    <div className={`font-bold ${
                      value.includes('LOW') ? 'text-green-700' :
                      value.includes('MODERATE') ? 'text-yellow-700' :
                      value.includes('HIGH') && !value.includes('(') ? 'text-red-700' :
                      'text-blue-700'
                    }`}>
                      {value}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="table-container">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Cash Flow Schedule (First 10 Days)
            </h3>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Opening</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Inflows</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Outflows</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Closing</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data.cash_flow_schedule.slice(0, 10).map((row) => (
                    <tr key={row.period} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">{row.date}</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-900">
                        {formatCurrency(row.opening_balance)}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-green-600">
                        <div className="flex items-center justify-end">
                          <TrendingUp className="w-4 h-4 mr-1" />
                          {formatCurrency(row.inflows)}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-red-600">
                        <div className="flex items-center justify-end">
                          <TrendingDown className="w-4 h-4 mr-1" />
                          {formatCurrency(row.outflows)}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-semibold text-gray-900">
                        {formatCurrency(row.closing_balance)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
        </div>
      )}

      {/* Simulation Tab */}
      {activeTab === 'simulation' && (
        <CashFlowSimulation />
      )}
    </div>
  );
};

export default CashFlowModule;
